import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthBootstrap from "../auth/useAuthBootstrap";
import {
  Avatar,
  Button,
  Card,
  DesignV2Scope,
  Skeleton,
  StickyPageHeader,
} from "../design-v2";
import { MetricTile } from "../components/product";
import {
  SEGMENT_TONE_EXTRA,
  SEGMENT_TONE_PLANNED,
} from "../components/product/metricSegments";
import { getHomeDashboard, getUserSettings } from "../services/api";
import { getOpenWorkoutExecutions } from "../features/workoutLog/api/workoutLogApi";
import { isWorkoutLogEnabled } from "../features/workoutLog/domain/featureFlag";
import {
  formatWorkoutStats,
  trainAgainPath,
  workoutDetailsPath,
} from "../features/workoutLog/domain/workoutDetails";
import {
  isAcknowledgedSaved,
  selectServerOpenExecution,
  summarizeLocalRecords,
} from "../features/workoutLog/home/homeLocalState";
import { workoutLogStore } from "../features/workoutLog/store/localExecutionStore";
import { workoutSyncEngine } from "../features/workoutLog/sync/syncEngine";
import {
  FOCUS_ROLES,
  buildSelectedDayFocus as buildDayFocus,
  focusEntryKey,
  listFocusEntries,
} from "./homeDayFocus";
import { getHomeGreeting } from "./homeGreeting";
import SettingsDrawer from "../features/settings/SettingsDrawer";
import {
  SETTINGS_SCREEN_IDS,
  WORKOUT_PREFERENCES_SECTION_ID,
  resolveExecutionSettingsReturnTo,
} from "../features/settings/settingsNavigation";
import "./homeDashboard.css";

// Home distinguishes four states. `unknown` is never allowed to borrow the copy
// of `empty`: while the dashboard is unresolved the page asserts nothing about
// the user's program (D15).
const HOME_STATE = Object.freeze({
  UNKNOWN: "unknown",
  EMPTY: "empty",
  LOADED: "loaded",
  ERROR: "error",
});

function getLocalDateInput() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDayLabel(dateKey) {
  return new Date(`${dateKey}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "narrow",
    timeZone: "UTC",
  });
}

function formatDayNumber(dateKey) {
  return new Date(`${dateKey}T00:00:00Z`).getUTCDate();
}

// The markers are the server's (`markers.planned`, `markers.extra`).
function describeDayMarkers(markers) {
  const parts = [
    markers?.planned ? "Planned workout" : null,
    markers?.extra ? "Extra workout" : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Rest day";
}

function formatWeekdayName(dateKey) {
  return new Date(`${dateKey}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });
}

function readCount(value) {
  return Math.max(0, Number(value) || 0);
}

// One Weekly Performance tile (blueprint §16.3, §16.4). Presentation only: the
// planned / extra / remaining / logged counts are the server's.
function WeeklyMetricTile({ label, metric, hasPlannedWork, logged }) {
  const planned = readCount(metric?.planned);
  // Decided per metric: a week can plan workouts with no countable planned
  // set (cardio, warm-up only). Never 0/0 and no bar for that metric (§16.4).
  if (!hasPlannedWork || planned === 0) {
    return (
      <MetricTile label={label} value={String(logged)} note="Logged this week" showBar={false} />
    );
  }

  const plannedCompleted = readCount(metric?.plannedCompleted);
  const extra = readCount(metric?.extra);
  const remaining = readCount(metric?.remaining);

  return (
    <MetricTile
      label={label}
      valueParts={{ primary: String(plannedCompleted), secondary: `/${planned}` }}
      barMax={planned}
      barSegments={[
        { value: plannedCompleted, tone: SEGMENT_TONE_PLANNED },
        { value: extra, tone: SEGMENT_TONE_EXTRA, label: extra > 0 ? `${extra} extra` : null },
      ]}
      note={
        <>
          {extra > 0 ? (
            <>
              <span className="lz-home__metric-extra">+{extra} extra</span>
              {" · "}
            </>
          ) : null}
          <span className="lz-home__metric-remaining">
            {remaining > 0 ? `${remaining} remaining` : "All done"}
          </span>
        </>
      }
    />
  );
}

const NO_LOCAL_STATE = Object.freeze({ openExecution: null, pendingSavedCount: 0 });
const TRAIN_HUB_PATH = "/train";

// The existing Start contract (TrainHub `occurrenceStartPath`): the route reads
// the CYCLE_OCCURRENCE target from the query and the projection, for the Extra
// interstitial, from the navigation state.
function occurrenceStartPath(startTarget) {
  const params = new URLSearchParams({
    kind: "CYCLE_OCCURRENCE",
    cycleId: startTarget.cycleId,
    occurrenceKey: startTarget.occurrenceKey,
  });
  return `/train/start?${params}`;
}

// Same formatter as Workout Details: one presentation per saved execution.
function formatExecutionStats(execution) {
  return formatWorkoutStats(execution).join(" · ") || null;
}

// Presentation of one focus entry. Every fact (role, outcome, cycle, stats)
// is read from the server's item; nothing is classified here.
function describeFocusEntry(entry) {
  const { role, item } = entry;
  if (role === FOCUS_ROLES.EXECUTION) {
    const extra = item.execution.outcome === "EXTRA";
    return {
      tag: extra ? "Extra Workout" : "Completed",
      tone: extra ? "info" : "neutral",
      title: item.execution.title,
      subtitle: item.execution.sourceLabel,
      stats: formatExecutionStats(item.execution),
      note: null,
    };
  }
  const { occurrence } = item;
  const subtitle = `Workout ${occurrence.orderIndex} · Week ${occurrence.weekNumber}`;
  if (role === FOCUS_ROLES.SATISFIED_OCCURRENCE) {
    return {
      tag: "Completed",
      tone: "neutral",
      title: occurrence.workoutName,
      subtitle,
      stats: formatExecutionStats(occurrence.execution),
      note: null,
    };
  }
  if (role === FOCUS_ROLES.PREVIEW_OCCURRENCE) {
    return {
      tag: "Upcoming cycle",
      tone: "neutral",
      title: occurrence.workoutName,
      subtitle,
      stats: null,
      note: occurrence.cycleName
        ? `Part of ${occurrence.cycleName}. Available when that cycle starts.`
        : "Available when its cycle starts.",
    };
  }
  return { tag: "Planned", tone: "action", title: occurrence.workoutName, subtitle, stats: null, note: null };
}

// Every saved execution a dashboard response shows on its rail.
function listDashboardExecutionIds(response) {
  const ids = new Set();
  (response?.schedule14Days?.days || []).forEach((day) => {
    (day.items || []).forEach((item) => {
      const executionId =
        item.kind === "EXECUTION" ? item.execution?.executionId : item.occurrence?.execution?.executionId;
      if (executionId) ids.add(executionId);
    });
  });
  return ids;
}

// The saved execution an entry's Details / Train again act on. Never inferred:
// an entry without a real `executionId` lists neither action.
function entryExecutionId(entry) {
  const { item } = entry;
  return item.kind === "EXECUTION"
    ? item.execution.executionId
    : item.occurrence.execution?.executionId || null;
}

// The hero's actions, exactly as its model lists them. `enabled` is the
// Workout Log exposure flag (§21.4), which gates the actions themselves.
function HeroActions({ entry, enabled, onStart, onDetails, onTrainAgain }) {
  const { actions, item } = entry;
  if (actions.includes("START_SESSION")) {
    return (
      <Button
        variant="primary"
        size="lg"
        className="lz-home__hero-cta"
        disabled={!enabled}
        data-startable="true"
        onClick={() => onStart(item.occurrence)}
      >
        Start Session
        <ArrowRightIcon />
      </Button>
    );
  }
  if (actions.includes("DETAILS") || actions.includes("TRAIN_AGAIN")) {
    const executionId = entryExecutionId(entry);
    return (
      <div className="lz-home__actions">
        {actions.includes("DETAILS") ? (
          <Button
            variant="secondary"
            size="lg"
            disabled={!enabled}
            data-action="DETAILS"
            onClick={() => onDetails(executionId)}
          >
            Details
          </Button>
        ) : null}
        {actions.includes("TRAIN_AGAIN") ? (
          <Button
            variant="secondary"
            size="lg"
            disabled={!enabled}
            data-action="TRAIN_AGAIN"
            aria-label={`Train again: ${describeFocusEntry(entry).title}`}
            onClick={() => onTrainAgain(executionId)}
          >
            Train again
          </Button>
        ) : null}
      </div>
    );
  }
  return null;
}

function PagerChevron({ direction }) {
  return (
    <svg className="lz-home__pager-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={direction === "previous" ? "m15 5-7 7 7 7" : "m9 5 7 7-7 7"} />
    </svg>
  );
}

// Which of the selected day's workouts the Focus card shows. Only rendered for
// a day with several items; the buttons make the swipe optional.
function FocusPager({ index, count, title, onStep }) {
  const atStart = index === 0;
  const atEnd = index === count - 1;
  return (
    <div
      className="lz-home__pager"
      role="group"
      aria-label="Workouts on this day"
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") onStep(-1);
        if (event.key === "ArrowRight") onStep(1);
      }}
    >
      {/* aria-disabled, not disabled: the button keeps focus at either end. */}
      <button
        type="button"
        className="lz-home__pager-button"
        aria-label="Previous workout"
        aria-disabled={atStart}
        onClick={() => onStep(-1)}
      >
        <PagerChevron direction="previous" />
      </button>
      <span className="lz-home__pager-position" aria-hidden="true">
        {index + 1} / {count}
      </span>
      <button
        type="button"
        className="lz-home__pager-button"
        aria-label="Next workout"
        aria-disabled={atEnd}
        onClick={() => onStep(1)}
      >
        <PagerChevron direction="next" />
      </button>
      <span className="lz-v2-visually-hidden" aria-live="polite" data-testid="home-focus-position">
        Workout {index + 1} of {count}: {title}
      </span>
    </div>
  );
}

const SWIPE_MIN_DISTANCE = 40;

// LEGACY single-session port, kept for one release with the server's
// `days[].session` field (§21.5) and still pinned by the legacy contract cases.
// Home itself no longer renders from it: Phase 10 reads `days[].items`.
export function buildRestFocus(selectedDateKey, currentProgram) {
  return {
    date: selectedDateKey,
    type: "rest",
    title: "Rest / Recovery",
    subtitle: currentProgram?.name || "No session planned",
    showStartSession: false,
    session: null,
  };
}

export function buildSelectedDayFocus(selectedDateKey, session, currentProgram) {
  if (!session) {
    return buildRestFocus(selectedDateKey, currentProgram);
  }

  return {
    date: selectedDateKey,
    type: "session",
    title: session.workoutName,
    subtitle: `Workout ${session.orderIndex} · Week ${session.weekNumber}`,
    showStartSession: session.status === "PLANNED" || session.status === "RESCHEDULED",
    session,
  };
}

/* Feature-local inline icons. D25 keeps Design V2 on inline SVG and forbids a
 * new icon dependency; none of these is a repeated product concept, so per the
 * component-taxonomy rule they stay in this page rather than becoming shared
 * components. */
function BellIcon() {
  return (
    <svg className="lz-home__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9.5a6 6 0 0 1 12 0c0 3.9 1.1 5.5 1.9 6.4H4.1C4.9 15 6 13.4 6 9.5Z" />
      <path d="M9.9 19.2a2.2 2.2 0 0 0 4.2 0" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg className="lz-home__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.25" />
      <circle cx="12" cy="12" r="7.25" />
      <path d="M12 2.6v2.1M12 19.3v2.1M21.4 12h-2.1M4.7 12H2.6M18.65 5.35l-1.48 1.48M6.83 17.17l-1.48 1.48M18.65 18.65l-1.48-1.48M6.83 6.83 5.35 5.35" />
    </svg>
  );
}

function BoltIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.4 2 4.6 13.7h5.7L9.1 22l9.3-12.2h-5.9L13.4 2Z" />
    </svg>
  );
}

function SparkleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10 2.6 1.9 5.2 5.2 1.9-5.2 1.9L10 16.8l-1.9-5.2L2.9 9.7l5.2-1.9L10 2.6Z" />
      <path d="m18 14.2.95 2.6 2.6.95-2.6.95-.95 2.6-.95-2.6-2.6-.95 2.6-.95.95-2.6Z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg className="lz-home__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.5 12h14M13 6.5l5.5 5.5-5.5 5.5" />
    </svg>
  );
}

function HomeLoading() {
  return (
    <DesignV2Scope
      className="lz-home__loading"
      aria-busy="true"
      data-testid="home-loading"
    >
      <Skeleton
        shape="block"
        width={180}
        height={26}
        aria-label="Loading program status"
      />
      <Skeleton shape="block" height={286} aria-label="Loading selected day focus" />
      <div className="lz-home__loading-days" data-testid="home-loading-schedule">
        {Array.from({ length: 14 }, (_, index) => (
          <Skeleton key={index} shape="block" height={56} />
        ))}
      </div>
      <Skeleton shape="block" height={140} aria-label="Loading weekly performance" />
      <Skeleton shape="block" height={96} aria-label="Loading coach insight" />
    </DesignV2Scope>
  );
}

function HomeErrorPanel({ message, onRetry }) {
  return (
    <div role="alert" className="lz-home__error">
      <p>{message}</p>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

export default function HomeDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthBootstrap();
  const [dashboard, setDashboard] = useState(null);
  const [loadStatus, setLoadStatus] = useState("unknown");
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => getLocalDateInput());
  // The same-day item the user moved to, kept only for the date it was chosen
  // on: any other date shows its canonical primary item.
  const [dayItemSelection, setDayItemSelection] = useState(null);
  const swipeStartRef = useRef(null);
  const [fixedScheduleDays, setFixedScheduleDays] = useState([]);
  const [localState, setLocalState] = useState(NO_LOCAL_STATE);
  const [serverOpenExecution, setServerOpenExecution] = useState(null);
  const dashboardRequestRef = useRef(0);
  // Who owns the dashboard right now. A foreground load (first load, Retry)
  // owns the loading / error resolution; a background refresh exists only
  // once Home is resolved and never supersedes a foreground load.
  const foregroundRef = useRef("pending");
  const refreshRef = useRef({ inFlight: false, queued: false });
  const refreshedAcksRef = useRef(new Set());
  // Executions acknowledged while a foreground load was unresolved.
  const deferredAcksRef = useRef(new Set());
  const userId = user?.id || null;
  // The Workout Log exposure flag (§21.4): Start Session, Resume and the
  // local sync surfaces exist only where the Workout Log is exposed.
  const workoutLogEnabled = isWorkoutLogEnabled();
  const [profileAvatar, setProfileAvatar] = useState({ url: null, sex: null });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialScreen, setSettingsInitialScreen] = useState(null);
  const [settingsNotice, setSettingsNotice] = useState(null);
  const [settingsReturnTo, setSettingsReturnTo] = useState(null);

  const displayName =
    typeof user?.displayName === "string" ? user.displayName.trim() : "";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedSettings = params.get("settings");
    if (!["account", WORKOUT_PREFERENCES_SECTION_ID].includes(requestedSettings)) {
      return;
    }
    setSettingsInitialScreen({
      id: SETTINGS_SCREEN_IDS.SECTION,
      params: { sectionId: requestedSettings },
    });
    setSettingsNotice(requestedSettings === "account" ? params.get("link") : null);
    setSettingsReturnTo(
      requestedSettings === WORKOUT_PREFERENCES_SECTION_ID
        ? resolveExecutionSettingsReturnTo(location.state)
        : null
    );
    setIsSettingsOpen(true);
    navigate(location.pathname, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  const closeSettings = useCallback(() => {
    const returnTo = settingsReturnTo;
    setIsSettingsOpen(false);
    setSettingsInitialScreen(null);
    setSettingsNotice(null);
    setSettingsReturnTo(null);
    if (returnTo) navigate(returnTo, { replace: true });
  }, [navigate, settingsReturnTo]);

  // The authoritative refresh after a sync acknowledgement (D3). Background
  // only: it runs once Home is resolved and replaces the server data in place:
  // no skeleton, no reload, and the selected day is kept while it is still
  // inside the returned window. A failure leaves the current Home untouched.
  // Acknowledgements landing during a refresh collapse into exactly one
  // follow-up request.
  const refreshDashboard = useCallback(async () => {
    const state = refreshRef.current;
    if (foregroundRef.current !== "resolved") {
      return;
    }
    if (state.inFlight) {
      state.queued = true;
      return;
    }
    state.inFlight = true;
    try {
      do {
        state.queued = false;
        dashboardRequestRef.current += 1;
        const request = dashboardRequestRef.current;
        try {
          const response = await getHomeDashboard(getLocalDateInput());
          if (request === dashboardRequestRef.current) {
            const days = response?.schedule14Days?.days || [];
            setDashboard(response);
            setFixedScheduleDays(days);
            setSelectedDate((current) =>
              days.some((day) => day.date === current)
                ? current
                : response?.selectedDate || getLocalDateInput()
            );
            setError("");
            setLoadStatus("resolved");
          }
        } catch (_refreshError) {
          // Keep what is on screen; the next acknowledgement or visit retries.
        }
      } while (state.queued && foregroundRef.current === "resolved");
    } finally {
      state.inFlight = false;
      state.queued = false;
    }
  }, []);

  // The foreground load: one request per visit, and one per Retry. The
  // response carries the whole visible schedule window, and day selection
  // never re-enters this effect (Rule P3, D16).
  useEffect(() => {
    let isMounted = true;
    dashboardRequestRef.current += 1;
    const request = dashboardRequestRef.current;
    foregroundRef.current = "pending";
    deferredAcksRef.current = new Set();

    async function loadDashboard() {
      try {
        const response = await getHomeDashboard(getLocalDateInput());
        if (!isMounted || request !== dashboardRequestRef.current) {
          return;
        }
        setDashboard(response);
        setFixedScheduleDays(response?.schedule14Days?.days || []);
        setSelectedDate(response?.selectedDate || getLocalDateInput());
        setError("");
        setLoadStatus("resolved");
        foregroundRef.current = "resolved";
        // This request may have been answered before an acknowledgement that
        // landed while it was in flight. Refresh once only when the response
        // does not show one of those executions.
        const deferred = deferredAcksRef.current;
        deferredAcksRef.current = new Set();
        if (deferred.size > 0) {
          const shown = listDashboardExecutionIds(response);
          if ([...deferred].some((executionId) => !shown.has(executionId))) {
            void refreshDashboard();
          }
        }
      } catch (loadError) {
        if (!isMounted || request !== dashboardRequestRef.current) {
          return;
        }
        setDashboard(null);
        setFixedScheduleDays([]);
        setError(loadError.message || "Unable to load home dashboard.");
        setLoadStatus("error");
        // Retry is the way out; it fetches after every acknowledgement so far.
        foregroundRef.current = "error";
        deferredAcksRef.current = new Set();
      }
    }

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [refreshDashboard, retryKey]);

  // Local Workout Log state (read-only): the open execution for the Resume
  // card and the SAVED workouts still waiting to sync. No network.
  const readLocalRecords = useCallback(async () => {
    if (!userId || !workoutLogEnabled) return [];
    try {
      return (await workoutLogStore.listExecutions(userId)) || [];
    } catch (_storeError) {
      return [];
    }
  }, [userId, workoutLogEnabled]);

  useEffect(() => {
    let isMounted = true;
    setLocalState(NO_LOCAL_STATE);
    setServerOpenExecution(null);
    if (!userId || !workoutLogEnabled) {
      return undefined;
    }

    (async () => {
      const records = await readLocalRecords();
      if (!isMounted) return;
      const summary = summarizeLocalRecords(records);
      setLocalState(summary);
      // Local first; the server's open list is only a fallback (a workout
      // started on another device, or one this device no longer holds).
      if (summary.openExecution || (typeof navigator !== "undefined" && navigator.onLine === false)) {
        return;
      }
      try {
        const response = await getOpenWorkoutExecutions();
        if (isMounted) {
          setServerOpenExecution(selectServerOpenExecution(response?.executions, records));
        }
      } catch (_openError) {
        // Non-blocking: Home stays complete without the fallback.
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [readLocalRecords, userId, workoutLogEnabled]);

  // The sync engine's own events (no polling). An accepted push re-reads the
  // local state; when it acknowledged a SAVED workout, Home refetches once so
  // the server's classification replaces the pending notice.
  useEffect(() => {
    if (!userId || !workoutLogEnabled) {
      return undefined;
    }
    let isMounted = true;
    const unsubscribe = workoutSyncEngine.subscribe((event) => {
      const accepted = event?.type === "SYNC_ACCEPTED" && event.userId === userId;
      if (!accepted && event?.type !== "EXECUTION_QUARANTINED") {
        return;
      }
      readLocalRecords().then((records) => {
        if (!isMounted) return;
        setLocalState(summarizeLocalRecords(records));
        if (!accepted) return;
        const record = records.find((entry) => entry.executionId === event.executionId);
        if (!isAcknowledgedSaved(record)) return;
        const acknowledgement = `${record.executionId}:${record.ackedRevision}`;
        if (refreshedAcksRef.current.has(acknowledgement)) return;
        refreshedAcksRef.current.add(acknowledgement);
        // Its server copy is SAVED now: it is no longer an open execution.
        setServerOpenExecution((current) =>
          current?.executionId === record.executionId ? null : current
        );
        if (foregroundRef.current === "pending") {
          // The foreground load is already fetching and owns the outcome.
          deferredAcksRef.current.add(record.executionId);
          return;
        }
        void refreshDashboard();
      });
    });
    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [readLocalRecords, refreshDashboard, userId, workoutLogEnabled]);

  // The authoritative avatar is exposed by the existing Settings/Profile read
  // path only, so it is resolved on its own request that never gates, delays or
  // sequences the dashboard (AV2, AV3). No AuthBootstrap or /api/auth/session
  // change, and no new endpoint (AV1, AV5).
  useEffect(() => {
    let isMounted = true;

    getUserSettings()
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setProfileAvatar({
          url: response?.account?.profile?.avatar?.url || null,
          sex: response?.account?.profile?.sex || null,
        });
      })
      .catch(() => {
        // Non-blocking by contract: Home stays complete and correct on the
        // canonical placeholder (AV3, AV4).
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleRetry = useCallback(() => {
    foregroundRef.current = "pending";
    setLoadStatus("unknown");
    setError("");
    setRetryKey((value) => value + 1);
  }, []);

  const isResolved = loadStatus === "resolved";
  const status = isResolved ? dashboard?.status || null : null;
  const currentProgram = isResolved ? dashboard?.currentProgram || null : null;
  const weeklyPerformance = isResolved ? dashboard?.weeklyPerformance || null : null;

  const homeState = useMemo(() => {
    if (loadStatus === "error") {
      return HOME_STATE.ERROR;
    }
    if (!isResolved) {
      return HOME_STATE.UNKNOWN;
    }
    // Empty only when there is genuinely nothing to show: saved workouts keep
    // Home loaded with or without a cycle (C-6).
    const hasItems = (dashboard?.schedule14Days?.days || []).some(
      (day) => (day.items || []).length > 0
    );
    const hasLoggedWork =
      readCount(dashboard?.weeklyPerformance?.loggedWorkouts) > 0 ||
      readCount(dashboard?.weeklyPerformance?.loggedSets) > 0;
    return dashboard?.currentProgram || hasItems || hasLoggedWork
      ? HOME_STATE.LOADED
      : HOME_STATE.EMPTY;
  }, [dashboard, isResolved, loadStatus]);

  const scheduleDays = useMemo(
    () =>
      fixedScheduleDays.map((day) => ({
        ...day,
        isSelected: day.date === selectedDate,
      })),
    [fixedScheduleDays, selectedDate]
  );
  const selectedDay = useMemo(
    () => scheduleDays.find((day) => day.date === selectedDate) || null,
    [scheduleDays, selectedDate]
  );
  const isSelectedDateToday = Boolean(selectedDay?.isToday);
  // The selected day's v2 focus, derived locally from the items the server
  // built for that day: selecting a day issues no request.
  const todayFocus = useMemo(
    () =>
      isResolved
        ? buildDayFocus({
          selectedDate,
          items: selectedDay?.items || [],
          currentProgram,
        })
        : null,
    [currentProgram, isResolved, selectedDate, selectedDay]
  );
  const focusEntries = useMemo(() => listFocusEntries(todayFocus), [todayFocus]);
  const activeIndex =
    dayItemSelection?.date === selectedDate
      ? Math.max(0, focusEntries.findIndex((entry) => focusEntryKey(entry) === dayItemSelection.key))
      : 0;
  const activeEntry = focusEntries[activeIndex] || null;
  const activeView = activeEntry ? describeFocusEntry(activeEntry) : null;
  const openExecution = localState.openExecution || serverOpenExecution;
  const pendingSavedCount = localState.pendingSavedCount;

  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    setDayItemSelection(null);
  }, []);
  // Moves within the selected day only; the calendar date never changes here.
  const stepFocusItem = (delta) => {
    const next = Math.min(focusEntries.length - 1, Math.max(0, activeIndex + delta));
    if (next === activeIndex) return;
    setDayItemSelection({ date: selectedDate, key: focusEntryKey(focusEntries[next]) });
  };
  const handleFocusTouchStart = (event) => {
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    swipeStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const handleFocusTouchEnd = (event) => {
    const start = swipeStartRef.current;
    const touch = event.changedTouches[0];
    swipeStartRef.current = null;
    if (!start || !touch) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    // A clearly horizontal gesture; a vertical one stays the page scroll.
    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
    stepFocusItem(deltaX < 0 ? 1 : -1);
  };

  const handleStart = useCallback(
    (occurrence) => {
      if (!occurrence?.startTarget) return;
      navigate(occurrenceStartPath(occurrence.startTarget), { state: { occurrence } });
    },
    [navigate]
  );
  // The window is Monday-anchored by the server, but the letters are still read
  // off the loaded days so the row can never disagree with the grid beneath it.
  const weekdayLabels = useMemo(
    () => scheduleDays.slice(0, 7).map((day) => formatDayLabel(day.date)),
    [scheduleDays]
  );

  // Weekly Performance v2 (blueprint §16): every count comes from the server.
  const hasPlannedWork = Boolean(weeklyPerformance?.hasPlannedWork);
  const loggedWorkouts = readCount(weeklyPerformance?.loggedWorkouts);
  const loggedSets = readCount(weeklyPerformance?.loggedSets);
  const nothingLogged = !hasPlannedWork && loggedWorkouts === 0 && loggedSets === 0;
  const focusHeading = isSelectedDateToday ? "Today's Focus" : "Selected Day Focus";
  const handleDetails = useCallback(
    (executionId) => {
      if (executionId) navigate(workoutDetailsPath(executionId));
    },
    [navigate]
  );
  const handleTrainAgain = useCallback(
    (executionId) => {
      if (executionId) navigate(trainAgainPath(executionId));
    },
    [navigate]
  );
  const heroProgramLabel = currentProgram
    ? currentProgram.name
    : "Current program unavailable";
  // The program line belongs to the current program's own sessions and to a
  // rest day; a saved workout or an upcoming-cycle preview names its own source.
  const showHeroProgram =
    !activeEntry ||
    activeEntry.role === FOCUS_ROLES.STARTABLE_OCCURRENCE ||
    activeEntry.role === FOCUS_ROLES.SATISFIED_OCCURRENCE;
  const heroRole = activeEntry?.role || FOCUS_ROLES.REST;
  const heroFocusType = !activeEntry
    ? "rest"
    : activeEntry.item.kind === "EXECUTION" ? "execution" : "session";
  // Presentation only. buildRestFocus deliberately uses the program name as the
  // rest subtitle, and the hero already prints that name as its program label,
  // so on a rest day the two lines collide. The focus object is untouched: the
  // hero just swaps the duplicated line for a compact line about the day
  // itself, derived from the focus date already on screen. Rest is read from
  // the focus role, never from matching text.
  const heroSubtitle = !todayFocus
    ? null
    : activeView
      ? activeView.subtitle
      : currentProgram
        ? `Rest · ${formatWeekdayName(todayFocus.date)}`
        : todayFocus.subtitle;

  return (
    <DesignV2Scope className="lz-home" data-testid="home-root">
      <StickyPageHeader className="lz-home__header">
        <Avatar
          src={profileAvatar.url}
          sex={profileAvatar.sex}
          size={40}
          data-testid="home-profile-avatar"
        />

        <p className="lz-home__brand">
          Lyft <span className="lz-home__brand-accent">Zone</span>
        </p>

        <div className="lz-home__header-actions">
          <button
            type="button"
            className="lz-home__icon-button"
            aria-label="Notifications"
          >
            <BellIcon />
          </button>

          <button
            type="button"
            className="lz-home__icon-button"
            aria-label="Settings"
            onClick={() => setIsSettingsOpen(true)}
          >
            <GearIcon />
          </button>
        </div>
      </StickyPageHeader>

      <main className="lz-home__content" data-home-state={homeState}>
        {homeState === HOME_STATE.UNKNOWN ? <HomeLoading /> : null}

        {homeState === HOME_STATE.ERROR ? (
          <HomeErrorPanel message={error} onRetry={handleRetry} />
        ) : null}

        {isResolved ? (
          <>
            <div className="lz-home__intro">
              <div className="lz-home__greeting">
                <p className="lz-home__greeting-eyebrow">{getHomeGreeting()}</p>
                {displayName ? (
                  <h1 className="lz-home__greeting-name">{displayName}</h1>
                ) : null}
              </div>

              {status?.label ? (
                <p className="lz-home__plan">
                  <BoltIcon className="lz-home__plan-icon" />
                  <span className="lz-home__plan-label">{status.label}</span>
                </p>
              ) : null}
            </div>

            {pendingSavedCount > 0 ? (
              <p className="lz-home__sync-notice" role="status" data-testid="home-sync-notice">
                {pendingSavedCount === 1
                  ? "1 workout waiting to sync"
                  : `${pendingSavedCount} workouts waiting to sync`}
              </p>
            ) : null}

            {openExecution ? (
              <Card
                as="section"
                padding="md"
                className="lz-home__resume"
                aria-label="Workout in progress"
                data-testid="home-resume"
                data-source={openExecution.source}
              >
                <div className="lz-home__resume-text">
                  <p className="lz-home__resume-eyebrow">Workout in progress</p>
                  {openExecution.title ? (
                    <p className="lz-home__resume-title">{openExecution.title}</p>
                  ) : null}
                </div>
                {/* TRAIN owns recovery: it resumes a recent workout directly
                  * and applies the stale / other-device / other-tab rules. */}
                <Button
                  variant="primary"
                  size="md"
                  className="lz-home__resume-cta"
                  onClick={() => navigate(TRAIN_HUB_PATH)}
                >
                  Resume
                </Button>
              </Card>
            ) : null}

            {/* The one Focus card. Its zones are reserved, so its height and
              * everything below it stay put whatever the day or item shows. */}
            <Card
              as="section"
              padding="none"
              className="lz-home__hero"
              data-focus={heroFocusType}
              data-role={heroRole}
              data-testid="home-focus"
              aria-labelledby="lz-home-hero-title"
              onTouchStart={focusEntries.length > 1 ? handleFocusTouchStart : undefined}
              onTouchEnd={focusEntries.length > 1 ? handleFocusTouchEnd : undefined}
            >
              <div className="lz-home__hero-body">
                <div className="lz-home__hero-head">
                  <p className="lz-home__hero-eyebrow">{focusHeading}</p>
                  <div className="lz-home__hero-status">
                    {activeView ? (
                      <p className="lz-home__tag" data-tone={activeView.tone}>{activeView.tag}</p>
                    ) : null}
                    {focusEntries.length > 1 ? (
                      <FocusPager
                        index={activeIndex}
                        count={focusEntries.length}
                        title={activeView.title}
                        onStep={stepFocusItem}
                      />
                    ) : null}
                  </div>
                </div>

                <div className="lz-home__hero-title-zone">
                  <h2 className="lz-home__hero-title" id="lz-home-hero-title">
                    {activeView ? activeView.title : todayFocus?.title}
                  </h2>
                </div>

                <div className="lz-home__hero-meta">
                  {showHeroProgram ? (
                    <p className="lz-home__hero-program">{heroProgramLabel}</p>
                  ) : null}
                  {heroSubtitle ? <p className="lz-home__hero-subtitle">{heroSubtitle}</p> : null}
                  {activeView?.stats ? (
                    <p className="lz-home__hero-stats">{activeView.stats}</p>
                  ) : null}
                </div>

                <div className="lz-home__hero-footer">
                  {activeEntry ? (
                    <HeroActions
                      entry={activeEntry}
                      enabled={workoutLogEnabled}
                      onStart={handleStart}
                      onDetails={handleDetails}
                      onTrainAgain={handleTrainAgain}
                    />
                  ) : (
                    <Button
                      variant="secondary"
                      size="lg"
                      className="lz-home__hero-cta"
                      disabled
                      data-startable="false"
                    >
                      Rest Day
                    </Button>
                  )}
                  {/* A preview has no action: its reason takes that place. */}
                  {activeView?.note ? (
                    <p className="lz-home__hero-note">{activeView.note}</p>
                  ) : null}
                </div>
              </div>
            </Card>

            <section className="lz-home__section" aria-labelledby="lz-home-schedule-title">
              <div className="lz-home__section-heading">
                <h2 className="lz-home__section-title" id="lz-home-schedule-title">
                  Schedule
                </h2>
                <p className="lz-home__section-meta">Next 14 Days</p>
              </div>

              <div className="lz-home__weekdays" aria-hidden="true">
                {weekdayLabels.map((label, index) => (
                  <span className="lz-home__weekday" key={index}>
                    {label}
                  </span>
                ))}
              </div>

              <div className="lz-home__days">
                {scheduleDays.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    className="lz-home__day"
                    onClick={() => handleSelectDate(day.date)}
                    aria-pressed={day.isSelected}
                    aria-current={day.isToday ? "date" : undefined}
                    aria-label={`Select ${day.date}`}
                    aria-describedby={`lz-home-day-${day.date}`}
                    data-planned={day.markers?.planned ? "true" : "false"}
                    data-extra={day.markers?.extra ? "true" : "false"}
                    data-today={day.isToday ? "true" : "false"}
                  >
                    <span className="lz-home__day-number">
                      {formatDayNumber(day.date)}
                    </span>
                    <span className="lz-v2-visually-hidden" id={`lz-home-day-${day.date}`}>
                      {describeDayMarkers(day.markers)}
                    </span>
                    {/* Two kinds, side by side on one line (U). A day with
                      * neither keeps one muted dot so every cell aligns. */}
                    <span className="lz-home__day-markers" aria-hidden="true">
                      {day.markers?.planned ? (
                        <span className="lz-home__day-marker" data-marker="planned" />
                      ) : null}
                      {day.markers?.extra ? (
                        <span className="lz-home__day-marker" data-marker="extra" />
                      ) : null}
                      {!day.markers?.planned && !day.markers?.extra ? (
                        <span className="lz-home__day-marker" data-marker="none" />
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section
              className="lz-home__section"
              aria-labelledby="lz-home-performance-title"
            >
              <div className="lz-home__section-heading">
                <h2 className="lz-home__section-title" id="lz-home-performance-title">
                  Weekly Performance
                </h2>
                <p className="lz-home__section-meta">Current Week</p>
              </div>

              <div className="lz-home__metrics">
                {nothingLogged ? (
                  <Card className="lz-home__metric lz-home__metric--empty" padding="md">
                    <p className="lz-home__metric-empty" data-testid="home-metrics-empty">
                      No workouts yet this week
                    </p>
                  </Card>
                ) : (
                  <>
                    <Card className="lz-home__metric" padding="md">
                      <WeeklyMetricTile
                        label="Workouts"
                        metric={weeklyPerformance?.workouts}
                        hasPlannedWork={hasPlannedWork}
                        logged={loggedWorkouts}
                      />
                    </Card>

                    <Card className="lz-home__metric" padding="md">
                      <WeeklyMetricTile
                        label="Sets"
                        metric={weeklyPerformance?.sets}
                        hasPlannedWork={hasPlannedWork}
                        logged={loggedSets}
                      />
                    </Card>
                  </>
                )}
              </div>
            </section>

            <Card
              as="section"
              padding="md"
              className="lz-home__insight"
              aria-labelledby="lz-home-insight-title"
            >
              <h2 className="lz-home__insight-heading" id="lz-home-insight-title">
                <SparkleIcon className="lz-home__insight-icon" />
                AI Coach Insight
              </h2>
              <p className="lz-home__insight-body">
                {currentProgram
                  ? "Your Home now follows the published cycle and scheduled sessions. Select any day to preview its planned focus."
                  : "Create and publish a multi-week cycle to see your current program and upcoming sessions here."}
              </p>
            </Card>
          </>
        ) : null}
      </main>

      <SettingsDrawer
        isOpen={isSettingsOpen}
        initialScreen={settingsInitialScreen}
        notice={settingsNotice}
        closeOnInitialBack={Boolean(settingsReturnTo)}
        onClose={closeSettings}
      />
    </DesignV2Scope>
  );
}
